import type * as React from 'react';
import { CheckCircle, Info, Prohibit, Warning, WarningCircle } from '../icons';
import { cn } from '../lib/cn';

/**
 * Provider status, and any other state the person needs to act on.
 *
 * Colour plus words plus what to do next, never colour alone
 * (DESIGN.md "Components and patterns").
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TONES: Record<StatusTone, { wrapper: string; icon: typeof Info }> = {
  success: { wrapper: 'border-success-500/30 bg-success-50 text-success-700', icon: CheckCircle },
  warning: { wrapper: 'border-warning-500/30 bg-warning-50 text-warning-700', icon: Warning },
  danger: { wrapper: 'border-danger-500/30 bg-danger-50 text-danger-700', icon: WarningCircle },
  info: { wrapper: 'border-info-500/30 bg-info-50 text-info-700', icon: Info },
  neutral: { wrapper: 'border-border bg-navy-100 text-navy-900', icon: Prohibit },
};

export interface StatusBannerProps extends Omit<React.ComponentProps<'div'>, 'title'> {
  tone?: StatusTone;
  /** What the state is, in words. */
  title: React.ReactNode;
  /** What happens next, or what they should do. */
  description?: React.ReactNode;
  /** One action, at most. */
  action?: React.ReactNode;
}

function StatusBanner({ tone = 'info', title, description, action, className, ...props }: StatusBannerProps) {
  const { wrapper, icon: Glyph } = TONES[tone];

  return (
    <div
      data-slot="status-banner"
      data-tone={tone}
      role="status"
      className={cn('flex gap-3 rounded-(--radius-card) border p-4', wrapper, className)}
      {...props}
    >
      <Glyph size={22} weight="fill" className="mt-px shrink-0" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="font-heading text-body font-semibold">{title}</p>
        {description === undefined ? null : <p className="text-meta text-current">{description}</p>}
        {action === undefined ? null : <div className="mt-1">{action}</div>}
      </div>
    </div>
  );
}

export { StatusBanner };
